import React, { useRef, useState } from 'react';
import { FileText, FileCode, GripVertical, Pencil, Trash2 } from 'lucide-react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import FileNameInput from './FileNameInput';
import {
  PREVIEW_SURFACE,
  CARD_SHELL,
  DOC_ICON,
  DELETE_BADGE,
} from './fileCardStyles';
import { V2File } from '../v2Upload.d';
import ToolpathPreview from '@/components/common/ToolpathPreview';

// Native drag-and-drop — no library needed
interface FileCardProps {
  file: V2File;
  onDragStart?: (fileId: string) => void;
  isHot?: boolean;
  /** Show the name as plain text. Renaming belongs to Add files; elsewhere the
   * card is a thing you pick, and an input in it only competes for the click. */
  readOnlyName?: boolean;
  /** 1-based position in its controller column. The order is the order the
   * files are listed in, so it's worth showing — and it's what dragging a card
   * within a column changes. */
  index?: number;
  /** Open the file's detail view. When set, the preview becomes the button for
   * it — the name stays a rename field, so the two clicks don't fight. */
  onOpen?: () => void;
  /** Remove this file from the project. Shown as an × in the top-right, only
   * on hover so it doesn't clutter the preview at rest. */
  onDelete?: () => void;
}

function FilePreview({ file }: { file: V2File }) {
  if (file.kind === 'gcode') {
    if (!file.toolpathD) {
      return <FileCode className="w-8 h-8 text-muted-foreground/40" />;
    }
    return <ToolpathPreview d={file.toolpathD} />;
  }
  if (file.kind === 'image') {
    return (
      <img
        src={file.preview}
        alt={file.name}
        className="w-full h-full object-contain"
      />
    );
  }
  return <FileText className={DOC_ICON} />;
}

/** Interactive props for the preview when it opens a detail view — spread so a
 * non-opening card stays a plain div, with no button role competing for the
 * click on the rename field below it. */
function openProps(onOpen?: () => void) {
  if (!onOpen) return {};
  return {
    role: 'button',
    tabIndex: 0,
    onClick: onOpen,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onOpen();
    },
  };
}

/** The dragstart handler: sets the payload (a drag with an empty dataTransfer
 * won't start in Firefox/Safari — Chrome is the only browser lenient enough to
 * have hidden this) and notifies the parent. */
function makeDragStart(
  onDragStart: ((id: string) => void) | undefined,
  fileId: string
) {
  return (e: React.DragEvent) => {
    e.dataTransfer.setData('text/plain', fileId);
    e.dataTransfer.effectAllowed = 'move';
    onDragStart?.(fileId);
  };
}

function cardClass(draggable: boolean, isHot?: boolean): string {
  return cn(
    CARD_SHELL,
    draggable && 'cursor-grab active:cursor-grabbing',
    isHot && 'border-primary shadow-[0_0_0_1px_hsl(var(--primary))]'
  );
}

/** The badges/buttons floating over the preview: position index (top-left),
 * hover delete (top-right), and the hover "view details" hint. Each shows only
 * when its prop is set; delete/hint stay hidden at rest so they don't clutter. */
function PreviewOverlays({
  index,
  onOpen,
  onDelete,
}: {
  index?: number;
  onOpen?: () => void;
  onDelete?: () => void;
}) {
  return (
    <>
      {index !== undefined && (
        <span className="absolute top-1.5 left-1.5 w-5 h-5 rounded-md bg-primary text-white flex items-center justify-center text-[10px] font-semibold">
          {index}
        </span>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className={DELETE_BADGE}
          title="Delete file"
          aria-label="Delete file"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
      {onOpen && (
        <span className="pointer-events-none absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 rounded-full bg-foreground/80 px-2 py-0.5 text-[10px] font-semibold text-background opacity-0 transition-opacity duration-fast group-hover/preview:opacity-100">
          <Pencil className="w-2.5 h-2.5" />
          View details
        </span>
      )}
    </>
  );
}

function CardPreview({
  file,
  index,
  onOpen,
  onDelete,
}: {
  file: V2File;
  index?: number;
  onOpen?: () => void;
  onDelete?: () => void;
}) {
  return (
    <div
      {...openProps(onOpen)}
      className={cn(
        'group/preview relative aspect-[4/3] border-b border-border flex items-center justify-center overflow-hidden',
        PREVIEW_SURFACE,
        onOpen && 'cursor-zoom-in'
      )}
    >
      <FilePreview file={file} />
      <PreviewOverlays index={index} onOpen={onOpen} onDelete={onDelete} />
    </div>
  );
}

/** A clipped name gets its full text on hover. Measured on enter rather than on
 * render, so a resize can't leave a tooltip on a name that now fits. */
function TruncatedName({ name }: { name: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [clipped, setClipped] = useState(false);

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            ref={ref}
            onPointerEnter={() => {
              const el = ref.current;
              setClipped(!!el && el.scrollWidth > el.clientWidth);
            }}
            className="truncate text-[13px] font-semibold text-foreground"
          >
            {name}
          </div>
        </TooltipTrigger>
        {clipped && (
          <TooltipPrimitive.Portal>
            <TooltipContent className="max-w-[280px] break-all text-xs">
              {name}
            </TooltipContent>
          </TooltipPrimitive.Portal>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

/** The name, as plain text or a rename field. Renaming belongs to Add files;
 * elsewhere the card is a thing you pick, so the name reads as text. */
function NameCell({
  file,
  readOnlyName,
  onEditingChange,
}: {
  file: V2File;
  readOnlyName: boolean;
  onEditingChange: (v: boolean) => void;
}) {
  if (readOnlyName) {
    return <TruncatedName name={file.name} />;
  }
  return <FileNameInput file={file} onEditingChange={onEditingChange} />;
}

export default function FileCard({
  file,
  onDragStart,
  isHot,
  readOnlyName = false,
  index,
  onOpen,
  onDelete,
}: FileCardProps) {
  const [renaming, setRenaming] = useState(false);
  const draggable = Boolean(onDragStart);

  return (
    <div
      draggable={draggable && !renaming}
      onDragStart={makeDragStart(onDragStart, file.id)}
      // Keyed by name rather than id: a test knows the file it dropped, not the
      // client-side id the wizard minted for it.
      data-testid={`v2-file-card-${file.name}`}
      className={cardClass(draggable, isHot)}
    >
      <CardPreview
        file={file}
        index={index}
        onOpen={onOpen}
        onDelete={onDelete}
      />
      <div className="flex items-center gap-2 p-2.5">
        {/* The handle says the card moves. It isn't the drag target — the whole
            card is — it's just the thing that makes that obvious. */}
        {draggable && (
          <GripVertical className="w-3.5 h-3.5 shrink-0 text-faint" />
        )}
        <div className="min-w-0 flex-1">
          <NameCell
            file={file}
            readOnlyName={readOnlyName}
            onEditingChange={setRenaming}
          />
        </div>
      </div>
    </div>
  );
}
